import { Checkbox } from '@fluentui/react';
import { useContext, useEffect, useState } from 'react';

import AppContext from 'App/AppContext';
import {
  IPCheckbox,
  IPCommandBarButton,
  IPIconButton,
  IPLink,
  IPScrollablePane,
  IPSeparator,
  IPSpinner,
  IPText,
} from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { CriteriaFilterGroup } from 'components/AuditCriteria/Columns/criteriaFilter';
import Accordion from 'components/Accordion/Accordion';
import { ClassificationTagItem } from 'components/Classification/ClassificationTagItem';
import { ClassificationTagList } from 'components/Classification/ClassificationTagList';
import { IQuickFilterItemRenderer, QuickFilterContext } from 'components/Filter/QuickFilter';
import { GlobalFilterGroupKeys } from 'components/GlobalFilter/GlobalFilterGroupKeys';
import { clearFilterKeepGlobalFilter, countGlobalFilters } from 'components/GlobalFilter/GlobalFilterHelper';
import ClassificationPicker from 'components/Pickers/ClassificationPicker';
import EntityPicker from 'components/Pickers/EntityPicker';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import { OwnerItem, OwnerPicker } from 'components/Pickers/OwnerPicker';
import { FilterProcessingActivityGroupKeys } from 'components/ProcessingActivity/FilterProcessingActivityGroupKeys';
import { KeyValueTag } from 'components/Tags/KeyValueTag';
import { getEntity } from 'globalFunctions';
import {
  cancelIcon,
  filterICon,
  globalSceneBarItemStyles,
  globalStackStylesHeight100PaddingSmall,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { TFunction } from 'i18next';
import Asset from 'models/asset/asset';
import { AuthSchema } from 'models/auth/authSchema';
import { Role } from 'models/auth/role';
import Classification from 'models/classification/classification';
import Entity, { EntityTypes } from 'models/entity';
import { Owner } from 'models/owner';
import ProcessingActivity, {
  PrivacyFramework,
  ProcessingActivityRole,
  ProcessingActivityState,
} from 'models/processingActivity';
import { PrivacyFrameworkSetting } from 'models/setting';
import StringValue, { DataZoneKey, StringValueTypes } from 'models/stringValue/stringValue';
import Tag from 'models/tag';
import { TaskState } from 'models/tasks/taskHelperClasses';
import User from 'models/user';
import { useTranslation } from 'react-i18next';
import { apiGetAsset } from 'services/Api/assetService';
import { apiGetStringValuesShared } from 'services/Api/stringValueService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { getRandomId } from 'utils/string';

//
// Render the filter as a quick filter
// Each quick filter must be implemented here
// 1. When a quick filter cannot be rendered, return null so the filter is removed
// 2. When a quick filter needs to be loaded (e.g. control name), return a small <IPSpinner> while loading
//

export const QuickFilterProcessingActivitiesRenderer = (props: IQuickFilterItemRenderer): JSX.Element | null => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'processingActivities', 'processingActivity', 'tasks']);
  const [asset, setAsset] = useState<Asset | undefined>(undefined);
  const [hasLoadedAsset, setHasLoadedAsset] = useState<boolean>(false);
  //undefined means not loaded yet: keep showing the spinner instead of removing the quick filter
  const [classifications, setClassifications] = useState<Classification[] | undefined>(undefined);
  const [dataSubjectScales, setDataSubjectScales] = useState<StringValue[] | undefined>(undefined);

  const filterGroup = props.filter.split('$');
  const filterType = filterGroup[0] + '$';
  const filterValue = filterGroup[1];
  const dataAssetId =
    filterType === FilterProcessingActivityGroupKeys.dataAsset ? Number.parseInt(filterValue) : Number.NaN;

  useEffect(() => {
    let isActive = true;

    const loadAsset = async (assetId: number) => {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const newAsset = await apiGetAsset(assetId, accessToken, appContext.globalDataCache);

        if (isActive) setAsset(newAsset);
      } catch (err) {
        appContext.setError(err);
      } finally {
        if (isActive) setHasLoadedAsset(true);
      }
    };

    const loadClassifications = async () => {
      try {
        const classificationCol = await appContext.globalDataCache.classifications.getItems();

        if (isActive) setClassifications(classificationCol);
      } catch (err) {
        appContext.setError(err);
        if (isActive) setClassifications([]);
      }
    };

    const loadDataSubjectScales = async () => {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const newDataSubjectScales = await apiGetStringValuesShared(StringValueTypes.DataSubjectScale, accessToken);

        if (isActive) setDataSubjectScales(newDataSubjectScales);
      } catch (err) {
        appContext.setError(err);
        if (isActive) setDataSubjectScales([]);
      }
    };

    if (filterType === FilterProcessingActivityGroupKeys.dataAsset && dataAssetId > 0) {
      loadAsset(dataAssetId);
    }

    if (filterType === FilterProcessingActivityGroupKeys.classification) {
      loadClassifications();
    }

    if (filterType === FilterProcessingActivityGroupKeys.dataSubjectScale) {
      loadDataSubjectScales();
    }

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, dataAssetId]);

  switch (filterType) {
    case FilterProcessingActivityGroupKeys.owner:
      const owner = new Owner();
      owner.appContext = appContext;
      owner.setId(filterValue);
      const item = owner.item;
      if (!item) return null;

      return <OwnerItem owner={owner} />;
    case FilterProcessingActivityGroupKeys.status:
      //don't show archived in quick filter because the toolbar button handles this
      if (filterValue === ProcessingActivityState.Archived.toString()) return null;

      const processingActivityStateId = Number.parseInt(filterValue);
      if (processingActivityStateId !== 0 && !processingActivityStateId) return null;

      return <IPText>{ProcessingActivity.getStateText(processingActivityStateId, t as TFunction<string[]>)}</IPText>;
    case FilterProcessingActivityGroupKeys.role:
      const processingActivityTypeId = Number.parseInt(filterValue);
      if (processingActivityTypeId !== 0 && !processingActivityTypeId) return null;

      return <IPText>{ProcessingActivity.getRoleText(processingActivityTypeId, t as TFunction<string[]>)}</IPText>;
    case FilterProcessingActivityGroupKeys.dataAsset:
      if (isNaN(dataAssetId)) return null;
      if (dataAssetId > 0) {
        if (asset) {
          return <IPText>{asset.name}</IPText>;
        } else if (!hasLoadedAsset) {
          return <IPSpinner />;
        }
      } else {
        return <IPText>{t('processingActivities:TabOverview.Filter.NoAssets')}</IPText>;
      }

      return null;
    case FilterProcessingActivityGroupKeys.classification:
      if (!classifications) {
        return <IPSpinner />;
      } else {
        const classificationId = Number(filterValue);
        const classification = classifications.find((c) => c.classificationId === classificationId);
        if (!classification) return null;

        return <ClassificationTagItem classification={classification} />;
      }
    case FilterProcessingActivityGroupKeys.dataSubjectScale:
      if (!dataSubjectScales) {
        return <IPSpinner />;
      } else {
        const dataSubjectScaleId = Number(filterValue);
        const dataSubjectScale = dataSubjectScales.find((d) => d.stringValueId === dataSubjectScaleId);
        if (!dataSubjectScale) return null;

        return <IPText>{dataSubjectScale.value}</IPText>;
      }
    case FilterProcessingActivityGroupKeys.taskStatus:
      if (filterValue === 'open') {
        return <IPText>{t('tasks:Filter.StatusOpen')}</IPText>;
      } else {
        return <IPText>{appContext.globalDataCache.taskStates.get(Number(filterValue)).state}</IPText>;
      }
    case FilterProcessingActivityGroupKeys.transfers:
      const dataZoneKey = Number.parseInt(filterValue);
      if (isNaN(dataZoneKey)) return null;
      switch (dataZoneKey) {
        case DataZoneKey.TenantZone:
          return <IPText>{t('processingActivities:TabOverview.Filter.NoTransfersQuickFilter')}</IPText>;
        case DataZoneKey.Adequate:
          return <IPText>{t('processingActivities:TabOverview.Filter.EUAdequateQuickFilter')}</IPText>;
        case DataZoneKey.Other:
          return <IPText>{t('processingActivities:TabOverview.Filter.ThirdCountryQuickFilter')}</IPText>;
        default:
          return null;
      }
    case FilterProcessingActivityGroupKeys.tag:
      const tagId = Number(filterValue);
      if (!appContext.globalDataCache.tags.has(tagId)) return null;
      const tag = appContext.globalDataCache.tags.get(tagId);

      return <KeyValueTag tag={tag} />;
    case FilterProcessingActivityGroupKeys.shared:
      if (filterValue === 'everyone') {
        return <IPPersona size={IPPersonaSize.size24} text={t('adminAuth:AuthSchemaPicker.Placeholder')} />;
      } else {
        if (!appContext.globalDataCache.authSchemas.has(Number(filterValue))) return null;

        return (
          <IPPersona
            size={IPPersonaSize.size24}
            text={appContext.globalDataCache.authSchemas.get(Number(filterValue)).name}
          />
        );
      }
    case FilterProcessingActivityGroupKeys.standard:
      if (appContext.globalFilter.some((f) => f.startsWith(GlobalFilterGroupKeys.standard))) {
        return null;
      }

      const normId = Number.parseInt(filterValue);
      if (!appContext.globalDataCache.norms.has(normId)) return null;
      const norm = appContext.globalDataCache.norms.get(normId);

      return <IPText>{norm.name}</IPText>;
    default:
      return null;
  }
};

interface IFilterProcessingActivity {
  toggleDialog: (state: boolean) => void;
  isOpen: boolean;
  updateSelectedFilter: Function;
  selectedFilter: string[];
  dataAssets: Asset[];
  classifications: Classification[];
  dataSubjectScales: StringValue[];
}

const FilterProcessingActivities = (props: IFilterProcessingActivity) => {
  const { t } = useTranslation(['translation', 'processingActivities', 'adminAuth', 'tasks']);
  const appContext = useContext(AppContext);
  const [entityPickerClearKey, setEntityPickerClearKey] = useState<string>(getRandomId());
  const [showClassificationPicker, setShowClassificationPicker] = useState<boolean>(false);
  const entities = props.dataAssets.map((a) => getEntity(a));
  const privacyFramework =
    (appContext.globalDataCache.settings.get(PrivacyFrameworkSetting) as PrivacyFramework) ?? PrivacyFramework.GDPR;

  const handleFilterClear = () => {
    clearFilter();
    props.toggleDialog(false);
  };

  const clearFilter = () => {
    const newFilter = clearFilterKeepGlobalFilter(appContext, props.selectedFilter, FilterProcessingActivityGroupKeys);
    props.updateSelectedFilter(newFilter);
  };

  const handleSelectChange = (key: string) => {
    let items: string[] = [...props.selectedFilter];

    if (!props.selectedFilter.includes(key)) {
      items.push(key);
    } else {
      items = items.filter((i: string) => i !== key);
    }

    props.updateSelectedFilter(items);
  };

  const getSelectedFilterCount = (type: FilterProcessingActivityGroupKeys) => {
    if (props.selectedFilter.length > 0) {
      return props.selectedFilter.filter((f) => f.startsWith(type))?.length;
    } else {
      return 0;
    }
  };

  const getOwnerFromFilter = (filter: string[]): string | undefined => {
    const user = filter.find((f) => f.startsWith(FilterProcessingActivityGroupKeys.owner));

    return user?.split('$')[1];
  };

  const getTagsFromFilter = (filter: string[]): Tag[] => {
    const tags: Tag[] = [];
    filter.forEach((f) => {
      if (f.startsWith(FilterProcessingActivityGroupKeys.tag)) {
        const tagId = f.substring(4);
        const tag = appContext.globalDataCache.tags.get(Number.parseInt(tagId));
        tags.push(tag);
      }
    });

    return tags;
  };

  const getFilterCount = (filter: string[]): number => {
    const count = filter.length;

    return count - countGlobalFilters(appContext, filter, FilterProcessingActivityGroupKeys);
  };

  const getContextEntityFromFilter = (): Entity | undefined => {
    let entity: Entity | undefined = undefined;
    const filter = props.selectedFilter.find((f) => f.startsWith(FilterProcessingActivityGroupKeys.dataAsset));
    if (filter) {
      const id = Number(filter?.split('$')[1]);
      if (id > 0 && !isNaN(id)) {
        entity = new Entity();
        entity.entityId = id;
        entity.typeOfEntity = EntityTypes.Asset;
      }
    }

    return entity;
  };

  const removeFilterContext = () => {
    let items: string[] = [...props.selectedFilter];
    items = items.filter((f) => !f.startsWith(FilterProcessingActivityGroupKeys.dataAsset));
    props.updateSelectedFilter(items);
  };

  const updateFilterContext = (key: string) => {
    let items: string[] = [...props.selectedFilter];
    items = items.filter((f) => !f.startsWith(FilterProcessingActivityGroupKeys.dataAsset));
    items.push(key);
    props.updateSelectedFilter(items);
  };

  const getClassificationsFromFilter = (filter: string[]): Classification[] => {
    const classifications: Classification[] = [];
    filter.forEach((f) => {
      if (f.startsWith(FilterProcessingActivityGroupKeys.classification)) {
        const id = f.substring(15);
        const classification = props.classifications.find((n) => n.classificationId === Number.parseInt(id));
        if (classification) classifications.push(classification);
      }
    });

    return classifications;
  };

  const handleSelectChangeClassification = (keys: string[]) => {
    let items: string[] = [...props.selectedFilter];

    items = items.filter((i: string) => !i.startsWith(FilterProcessingActivityGroupKeys.classification));
    keys.forEach((key: string) => {
      items.push(key);
    });

    props.updateSelectedFilter(items);
  };

  //
  // Main render
  //
  const filterCount = getFilterCount(props.selectedFilter);

  return (
    <IPStack horizontal verticalAlign="center">
      <IPCommandBarButton
        iconProps={filterICon}
        className="calloutTargetFilterButton"
        text={t('processingActivities:TabOverview.Filter.Button', { count: filterCount })}
        onClick={() => props.toggleDialog(!props.isOpen)}
        styles={globalSceneBarItemStyles}
      />
      {filterCount > 0 && (
        <IPIconButton styles={globalSceneBarItemStyles} onClick={() => clearFilter()} iconProps={cancelIcon} />
      )}
      <IPCallout
        alignTargetEdge={true}
        gapSpace={0}
        target=".calloutTargetFilterButton"
        isBeakVisible={false}
        directionalHint={IPDirectionalHint.bottomLeftEdge}
        hidden={!props.isOpen}
        onDismiss={() => props.toggleDialog(false)}
      >
        <IPStack styles={globalStackStylesHeight100PaddingSmall} tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <IPStack horizontal horizontalAlign={'space-between'} styles={globalStackStylesHeight100PaddingSmall}>
              <IPText variant={'mediumPlus'}>{t('processingActivities:TabOverview.Filter.Header')}</IPText>
              <IPLink onClick={handleFilterClear} underline>
                {t('processingActivities:TabOverview.Filter.Clear')}
              </IPLink>
            </IPStack>
          </IPStack.Item>
          <IPStack.Item styles={{ root: { height: 400, width: 300, position: 'relative' } }}>
            <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
              <Accordion
                title={t('processingActivities:TabOverview.Filter.Role', {
                  count: getSelectedFilterCount(FilterProcessingActivityGroupKeys.role),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <Checkbox
                    key={ProcessingActivityRole.Controller}
                    label={t('processingActivity:Role.Controller')}
                    checked={props.selectedFilter.includes(
                      FilterProcessingActivityGroupKeys.role + ProcessingActivityRole.Controller,
                    )}
                    onChange={() =>
                      handleSelectChange(FilterProcessingActivityGroupKeys.role + ProcessingActivityRole.Controller)
                    }
                  />
                  <Checkbox
                    key={ProcessingActivityRole.Processor}
                    label={t('processingActivity:Role.Processor')}
                    checked={props.selectedFilter.includes(
                      FilterProcessingActivityGroupKeys.role + ProcessingActivityRole.Processor,
                    )}
                    onChange={() =>
                      handleSelectChange(FilterProcessingActivityGroupKeys.role + ProcessingActivityRole.Processor)
                    }
                  />
                </IPStack>
              </Accordion>

              <Accordion
                title={t('processingActivities:TabOverview.Filter.State', {
                  count: getSelectedFilterCount(FilterProcessingActivityGroupKeys.status),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <Checkbox
                    key={ProcessingActivityState.Concept}
                    label={t('processingActivity:State.Concept')}
                    checked={props.selectedFilter.includes(
                      FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Concept,
                    )}
                    onChange={() =>
                      handleSelectChange(FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Concept)
                    }
                  />
                  <Checkbox
                    key={ProcessingActivityState.Approved}
                    label={t('processingActivity:State.Approved')}
                    checked={props.selectedFilter.includes(
                      FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Approved,
                    )}
                    onChange={() =>
                      handleSelectChange(FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Approved)
                    }
                  />
                  <Checkbox
                    key={ProcessingActivityState.Archived}
                    label={t('processingActivity:State.Archived')}
                    checked={props.selectedFilter.includes(
                      FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Archived,
                    )}
                    onChange={() =>
                      handleSelectChange(FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Archived)
                    }
                  />
                  <Checkbox
                    key={ProcessingActivityState.AwaitingApproval}
                    label={t('processingActivity:State.AwaitingApproval')}
                    checked={props.selectedFilter.includes(
                      FilterProcessingActivityGroupKeys.status + ProcessingActivityState.AwaitingApproval,
                    )}
                    onChange={() =>
                      handleSelectChange(
                        FilterProcessingActivityGroupKeys.status + ProcessingActivityState.AwaitingApproval,
                      )
                    }
                  />
                </IPStack>
              </Accordion>

              <Accordion
                title={t('processingActivities:TabOverview.Filter.Owners', {
                  count: getSelectedFilterCount(FilterProcessingActivityGroupKeys.owner),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <OwnerPicker
                    hideLabel={true}
                    selectedItemId={getOwnerFromFilter(props.selectedFilter)}
                    onSelect={(item) => {
                      let id: string | undefined = undefined;
                      if (item instanceof User) {
                        id = item.id;
                      } else if (item instanceof Role) {
                        id = item.roleId;
                      } else if (!item) {
                        return handleSelectChange(
                          FilterProcessingActivityGroupKeys.owner + getOwnerFromFilter(props.selectedFilter),
                        );
                      }

                      handleSelectChange(FilterProcessingActivityGroupKeys.owner + id);
                    }}
                  />
                </IPStack>
              </Accordion>

              <Accordion
                title={t('processingActivities:TabOverview.Filter.DataAsset', {
                  count: getSelectedFilterCount(FilterProcessingActivityGroupKeys.dataAsset),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <EntityPicker
                    suggestionLimit={6}
                    entities={entities}
                    selectedEntity={getContextEntityFromFilter()}
                    addSelectedEntity={(item) => {
                      updateFilterContext(`${FilterProcessingActivityGroupKeys.dataAsset}${item.entityId}`);
                    }}
                    showHeader={false}
                    setSearchTextToSelectedEntity={true}
                    clearSearchText={() => {
                      removeFilterContext();
                    }}
                    isOnPanel={false}
                    clearKey={entityPickerClearKey}
                  />
                  <IPCheckbox
                    key={'no-assets'}
                    label={t('processingActivities:TabOverview.Filter.NoAssets')}
                    checked={props.selectedFilter.includes(FilterProcessingActivityGroupKeys.dataAsset + '0')}
                    onChange={(ev, checked) => {
                      if (checked) {
                        updateFilterContext(FilterProcessingActivityGroupKeys.dataAsset + '0');
                        setEntityPickerClearKey(getRandomId());
                      } else {
                        removeFilterContext();
                      }
                    }}
                  />
                </IPStack>
              </Accordion>

              <Accordion
                title={t('processingActivities:TabOverview.Filter.Classification', {
                  count: getSelectedFilterCount(FilterProcessingActivityGroupKeys.classification),
                })}
              >
                <IPStack
                  tokens={globalStackTokensGapSmall}
                  styles={{
                    root: {
                      minHeight: 100,
                      padding: 10,
                    },
                  }}
                >
                  <ClassificationTagList
                    classifications={getClassificationsFromFilter(props.selectedFilter)}
                    onRemove={(item: Classification) =>
                      handleSelectChange(
                        FilterProcessingActivityGroupKeys.classification + item.classificationId.toString(),
                      )
                    }
                  />
                  <IPStack.Item>
                    <IPLink id={'classification-picker'} onClick={() => setShowClassificationPicker(true)}>
                      {t('processingActivities:TabOverview.Filter.AddClassification')}
                    </IPLink>
                  </IPStack.Item>
                  <ClassificationPicker
                    isOpen={showClassificationPicker}
                    onDismiss={() => setShowClassificationPicker(false)}
                    target={`#classification-picker`}
                    onSelect={(items) => {
                      handleSelectChangeClassification(
                        items.map(
                          (item: Classification) =>
                            FilterProcessingActivityGroupKeys.classification + item.classificationId.toString(),
                        ),
                      );
                      setShowClassificationPicker(false);
                    }}
                    selectMultiple={true}
                    allowMultiplePerGroup={true}
                    preSelectedClassifications={getClassificationsFromFilter(props.selectedFilter)}
                  />
                </IPStack>
              </Accordion>

              <Accordion
                title={t('processingActivities:TabOverview.Filter.DataSubjectScale', {
                  count: getSelectedFilterCount(FilterProcessingActivityGroupKeys.dataSubjectScale),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  {props.dataSubjectScales.map((scale: StringValue) => (
                    <IPCheckbox
                      key={scale.stringValueId}
                      label={scale.value}
                      checked={props.selectedFilter.includes(
                        FilterProcessingActivityGroupKeys.dataSubjectScale + scale.stringValueId.toString(),
                      )}
                      onChange={() =>
                        handleSelectChange(
                          FilterProcessingActivityGroupKeys.dataSubjectScale + scale.stringValueId.toString(),
                        )
                      }
                    />
                  ))}
                </IPStack>
              </Accordion>

              <Accordion
                title={t('processingActivities:TabOverview.Filter.TaskStatus', {
                  count: getSelectedFilterCount(FilterProcessingActivityGroupKeys.taskStatus),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <IPCheckbox
                    key={'open'}
                    label={t('tasks:Filter.StatusOpen')}
                    checked={props.selectedFilter.includes(FilterProcessingActivityGroupKeys.taskStatus + 'open')}
                    onChange={() => handleSelectChange(FilterProcessingActivityGroupKeys.taskStatus + 'open')}
                  />
                  <IPSeparator styles={{ root: { height: 1 } }} />
                  {appContext.globalDataCache.taskStates.items.map((taskState: TaskState) => (
                    <IPCheckbox
                      key={taskState.taskStateId}
                      label={taskState.state}
                      checked={props.selectedFilter.includes(
                        FilterProcessingActivityGroupKeys.taskStatus + taskState.taskStateId.toString(),
                      )}
                      onChange={() =>
                        handleSelectChange(
                          FilterProcessingActivityGroupKeys.taskStatus + taskState.taskStateId.toString(),
                        )
                      }
                    />
                  ))}
                </IPStack>
              </Accordion>

              <Accordion
                title={ProcessingActivity.getTransfersTextLabel(
                  t,
                  privacyFramework,
                  getSelectedFilterCount(FilterProcessingActivityGroupKeys.transfers),
                )}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <Checkbox
                    key={DataZoneKey.TenantZone}
                    label={ProcessingActivity.getTransfersText(
                      DataZoneKey.TenantZone,
                      t as TFunction<string[]>,
                      privacyFramework,
                    )}
                    checked={props.selectedFilter.includes(
                      FilterProcessingActivityGroupKeys.transfers + DataZoneKey.TenantZone,
                    )}
                    onChange={() =>
                      handleSelectChange(FilterProcessingActivityGroupKeys.transfers + DataZoneKey.TenantZone)
                    }
                  />
                  <Checkbox
                    key={DataZoneKey.Adequate}
                    label={ProcessingActivity.getTransfersText(
                      DataZoneKey.Adequate,
                      t as TFunction<string[]>,
                      privacyFramework,
                    )}
                    checked={props.selectedFilter.includes(
                      FilterProcessingActivityGroupKeys.transfers + DataZoneKey.Adequate,
                    )}
                    onChange={() =>
                      handleSelectChange(FilterProcessingActivityGroupKeys.transfers + DataZoneKey.Adequate)
                    }
                  />
                  <Checkbox
                    key={DataZoneKey.Other}
                    label={ProcessingActivity.getTransfersText(
                      DataZoneKey.Other,
                      t as TFunction<string[]>,
                      privacyFramework,
                    )}
                    checked={props.selectedFilter.includes(
                      FilterProcessingActivityGroupKeys.transfers + DataZoneKey.Other,
                    )}
                    onChange={() => handleSelectChange(FilterProcessingActivityGroupKeys.transfers + DataZoneKey.Other)}
                  />
                </IPStack>
              </Accordion>

              {hasUserFeature(appContext, FeatureTypes.RBAC) && (
                <Accordion
                  title={t('adminAuth:AuthSchemaPicker.Filter.Shared', {
                    count: getSelectedFilterCount(FilterProcessingActivityGroupKeys.shared),
                  })}
                >
                  <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                    <Checkbox
                      key={'everyone'}
                      label={t('adminAuth:AuthSchemaPicker.Placeholder')}
                      checked={props.selectedFilter.includes(FilterProcessingActivityGroupKeys.shared + 'everyone')}
                      onChange={() => {
                        handleSelectChange(FilterProcessingActivityGroupKeys.shared + 'everyone');
                      }}
                    />
                    <IPSeparator styles={{ root: { height: 1 } }} />
                    {appContext.globalDataCache.authSchemas
                      .getItemsForMember(appContext.user.id)
                      .map((schema: AuthSchema) => (
                        <Checkbox
                          key={schema.authSchemaId}
                          label={schema.name}
                          checked={props.selectedFilter.includes(
                            FilterProcessingActivityGroupKeys.shared + schema.authSchemaId,
                          )}
                          onChange={() =>
                            handleSelectChange(FilterProcessingActivityGroupKeys.shared + schema.authSchemaId)
                          }
                        />
                      ))}
                  </IPStack>
                </Accordion>
              )}

              <Accordion
                title={t('processingActivities:TabOverview.Filter.Tags', {
                  count: getSelectedFilterCount(FilterProcessingActivityGroupKeys.tag),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <KeyValueTagPicker
                    maxTagWidth={250}
                    selectedTags={getTagsFromFilter(props.selectedFilter)}
                    onAdd={(item: Tag) =>
                      handleSelectChange(FilterProcessingActivityGroupKeys.tag + item.tagId.toString())
                    }
                    onRemove={(item: Tag) =>
                      handleSelectChange(FilterProcessingActivityGroupKeys.tag + item.tagId.toString())
                    }
                  />
                </IPStack>
              </Accordion>
              <CriteriaFilterGroup
                context={QuickFilterContext.processingActivities}
                selectedFilter={props.selectedFilter}
                onChange={handleSelectChange}
              />
            </IPScrollablePane>
          </IPStack.Item>
        </IPStack>
      </IPCallout>
    </IPStack>
  );
};

export default FilterProcessingActivities;
